import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { makeAuthenticatedApiCall } from '../../../apiHelper';
import { DEFAULT_DOCUMENT_QUEUE } from '../config/documentFlowConfig';
import { traceIa } from '../iaTrace';
import { CgDocType, CgOcrField, CgOcrResponse } from '../ocr/ocrResponseTypes';
// `import type` : effacé à la compilation, donc aucun cycle d'import à
// l'exécution (parcoursDetection dépend de crossCheckEngine, qui dépend de ce store).
import type { EntityKindOverrides, NatureChoisie } from '../validation/personneMorale';
import type { ParcoursSource } from '../v2/parcoursDetection';
import {
  type IdentityPerson,
  type IdentityPoolEntry,
  identityKindOf,
  identityPersonOf,
  identityPoolKey,
  isIdentitySlotType,
  routeIdentityDocs,
} from '../v2/identityRouting';
import {
  type CessionPoolEntry,
  buildCessionRefs,
  elireCession,
} from '../v2/cessionRouting';
import {
  KbisPoolEntry,
  KbisRefFields,
  isKbisDocType,
  routeKbisDocs,
} from '../v2/kbisRouting';

// Ré-export pour les consommateurs historiques (la définition vit désormais
// dans identityRouting pour éviter un import circulaire store ↔ routage).
export { docHasIdentityFace } from '../v2/identityRouting';
export type { IdentityPerson, IdentityPoolEntry } from '../v2/identityRouting';

/**
 * États du wizard FormCGIA.
 */
export type WizardStep =
  | 'intro'
  | 'uploading_doc'
  | 'validating_doc'
  | 'doc_rejected'
  | 'cross_checking'
  | 'review_form'
  | 'submitting'
  | 'submitted';

/**
 * Résultat OCR stocké pour un document uploadé avec succès.
 */
export interface UploadedDocResult {
  docType: CgDocType;
  firebaseUrls: string[]; // URLs des fichiers uploadés (1 ou 2)
  ocrResponse: CgOcrResponse; // réponse OCR de celui qui a matché
}

/**
 * Extraction agrégée (fusion cross-documents) : pour chaque champ cible du formulaire
 * final, on garde la meilleure source (confiance la plus haute).
 */
export interface ExtractedFieldEntry {
  fieldKey: string;
  field: CgOcrField;
  sourceDocType: CgDocType;
}

// ─────────────────────────────────────────────────────────────
// v2 : flow multi-dropzone avec classification IA en direct
// ─────────────────────────────────────────────────────────────

export type V2Step =
  | 'intro'
  | 'plate_and_dept' // étape 1 : immatriculation + département
  | 'tax_confirm' // étape 2 : confirmation du devis ECG Auto
  | 'ingesting' // étape 3 : upload + analyse OCR des pièces
  | 'verify' // étape 3.5 : vérification des informations extraites (liaison champ↔pièce)
  | 'recap' // étape 4 : récap final avant soumission
  | 'submitting'
  | 'submitted';

/**
 * Données ECG Auto retournées par /api/misiv/ecg, requises côté
 * TaxConfirmationDialog dès l'étape 2 (avant tout upload de pièce).
 */
export interface TaxDataSnapshot {
  taxeRegionale?: string | number;
  taxe35cv?: string | number;
  taxeParafiscale?: string | number;
  taxeCO2?: string | number;
  taxeMalus?: string | number;
  taxeSIV?: string | number;
  taxeRedevanceSIV?: string | number;
  taxeTotale?: string | number;
  vehicleDetails?: {
    VIN?: string;
    CO2?: string | number;
    puissance?: string | number;
    genre?: string;
    PTAC?: string | number;
    energie?: string;
    dateMEC?: string;
    /**
     * Marqué `true` par le backend (misivController.getECG) quand il a
     * substitué la plaque par un stub (BL-726-DJ par défaut, env non-prod).
     * Cf. crossCheckRules.isMisivStubResponse : permet de rétrograder
     * vin_misiv_mismatch / mec_misiv_mismatch en INFO sans liste de VINs
     * en dur côté front.
     */
    is_stub?: boolean;
  };
}

/**
 * Un fichier qui traverse le pipeline d'ingestion : upload Firebase → OCR auto-extract.
 * L'id est généré côté client à l'ajout dans la dropzone.
 */
export type IngestionStatus =
  | 'queued' // en attente d'upload
  | 'uploading' // upload Firebase en cours
  | 'analyzing' // OCR en cours
  | 'ok' // classifié + extrait avec un docType attendu
  | 'mismatch' // classifié mais pas dans les docs attendus (ex: doc en trop)
  | 'unreadable' // OCR a répondu unreadable=true
  | 'error'; // erreur réseau / upload / OCR

export interface IngestionItem {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  firebaseUrl?: string;
  status: IngestionStatus;
  docTypeDetected?: CgDocType | 'unknown';
  confidence?: number; // 0-100 (confiance de correspondance du TYPE de document)
  /**
   * Le modèle d'extraction typée a-t-il AFFIRMÉ que le document correspond au
   * type attendu (booléen explicite, distinct de la confiance numérique).
   * Sert de signal STABLE pour l'auto-validation : la confiance peut osciller
   * autour d'un seuil d'un run à l'autre (badge qui clignote), pas ce booléen.
   */
  docTypeMatch?: boolean;
  errorMessage?: string;
  /**
   * Raison DESTINÉE À L'AGENCE, quand on en connaît une formulée pour elle
   * (ex. « Fichier trop volumineux (21,3 Mo, maximum 15,0 Mo) »). Distinct de
   * `errorMessage`, qui reste le message technique brut pour le diagnostic.
   * Absent = on n'a rien de mieux qu'un libellé générique à afficher.
   */
  userFacingReason?: string;
  createdAt: number;
}

/**
 * Snapshot complet de l'état v2 d'une session — utilisé pour rehydrater le
 * store quand l'utilisateur bascule entre dossiers depuis le tracker.
 */
export interface V2SessionSnapshot {
  regPlate: string;
  department: string;
  taxData: TaxDataSnapshot | null;
  v2Step: V2Step;
  parcours: string | null;
  isSuccession: boolean;
  isProfessionalResale: boolean;
  entityKindOverrides?: EntityKindOverrides;
  ingestionItems: Record<string, IngestionItem>;
  uploadedDocs: Partial<Record<CgDocType, UploadedDocResult>>;
  extractedFields: Record<string, ExtractedFieldEntry>;
  /** Pool des pièces d'identité brutes (routées en slots par identityRouting). */
  identityDocsPool?: IdentityPoolEntry[];
}

/**
 * Une demande de redepôt côté GOD (1 ligne de cg_document_requests).
 * Sert à highlighter les pièces "À redéposer" dans le DocumentChecklist
 * et à brancher la phase finale fulfill au lieu de runPhase1+runPhase2.
 */
export interface OpenDocumentRequest {
  id: number;
  docType: CgDocType;
  reason: string | null;
  createdAt: string;
}

/**
 * Réponse de l'endpoint GET /api/cg/:id/agency-edit-context — pré-remplit
 * tout le store pour le mode édition (chip "Pièces à déposer" → FormCGIAv2).
 */
export interface AgencyEditContext {
  registrationId: number;
  regPlate: string;
  department: string;
  taxData: TaxDataSnapshot | null;
  invoicingType: 'One-time' | 'Bi-weekly' | 'Monthly' | string;
  nextStatusOnFulfill: 'awaiting_payment' | 'pending_god_review';
  uploadedDocs: Partial<Record<CgDocType, UploadedDocResult>>;
  extractedFields: Record<string, ExtractedFieldEntry>;
  openRequests: OpenDocumentRequest[];
  fulfilledRequests: Array<{ id: number; docType: CgDocType; fulfilledAt: string }>;
  parcours: string | null;
}

export interface AnalysisFindingSnapshot {
  ruleId: string;
  severity: 'error' | 'warning' | 'info';
  message?: string;
  documentTypes?: string[];
  /** Champs métier impactés — rejoués tels quels dans la colonne Alertes review. */
  fields?: string[];
}

/**
 * Résultat d'analyse figé à la soumission : l'union dédupliquée v1 → Opus →
 * regex EXACTEMENT telle que FormCGIA l'a calculée. `engine` trace quel moteur
 * a piloté le verdict. Persistée avec la CG, rejouée par la review.
 */
export interface AnalysisSnapshot {
  findings: AnalysisFindingSnapshot[];
  parcoursLabel: string | null;
  /**
   * Provenance du parcours : `agence` = case cochée à l'étape 1, sinon le
   * moteur qui l'a détecté. Absent des snapshots antérieurs à septembre 2026
   * (la review le rétro-déduit alors du libellé, cf.
   * `inferParcoursSourceFromLabel`).
   */
  parcoursSource?: ParcoursSource | null;
  engine: 'v1' | 'opus' | 'regex';
}

interface FormCGIAState {
  // ── Legacy wizard (conservé temporairement, non utilisé par v2) ──
  currentStep: WizardStep;
  docQueue: CgDocType[];
  currentDocIndex: number;
  rejectionReason: string | null;
  reuploadMode: CgDocType | null;

  // ── Partagés legacy ↔ v2 ──
  uploadedDocs: Partial<Record<CgDocType, UploadedDocResult>>;
  extractedFields: Record<string, ExtractedFieldEntry>;
  /**
   * Pool des pièces d'identité BRUTES (CNI / permis), une entrée par fichier.
   * Source de vérité du routage (personne × type × face) : les slots
   * `uploadedDocs.piece_identite*` / `permis_conduire*` sont DÉRIVÉS de ce pool
   * à chaque arrivée de pièce (cf. integrateDocResult / identityRouting).
   */
  identityDocsPool: IdentityPoolEntry[];
  /**
   * Pool des extraits Kbis, sur le même principe que `identityDocsPool` :
   * `uploadedDocs.kbis_vendeur` / `kbis_acheteur` en sont DÉRIVÉS à chaque
   * arrivée de pièce. Un dossier peut compter deux sociétés (13 des 26 dossiers
   * avec Kbis depuis juin 2026) et le slot unique d'avant faisait que le second
   * extrait écrasait le premier.
   */
  kbisDocsPool: KbisPoolEntry[];
  /**
   * Pièces d'identité dont la PERSONNE n'a pas pu être prouvée (verso muet
   * placé par défaut alors que plusieurs personnes étaient éligibles). L'UI y
   * propose une réassignation manuelle. Cf. `IdentityRoutingResult.ambiguousUrls`.
   */
  identityAmbiguousUrls: string[];
  hebergementUploads: HebergementUploads;
  kbisUrls: string[];
  /**
   * Pièces d'identité du co-titulaire, uploadées SANS OCR via un sous-bloc
   * dédié (le co-titulaire n'est pas classifié par le pipeline). Transmises en
   * `coBuyerFilesUrls` au backend → création de l'entité CoBuyer. Alimentées
   * uniquement quand un co-titulaire est déclaré/détecté (cobuyer_present).
   */
  coBuyerFiles: string[];
  /** Commentaire libre de l'agence pour Skipcar, saisi à l'étape de vérification. */
  agencyComment: string;
  /** Fichiers libres joints au commentaire agence (sans OCR). */
  agencyCommentFiles: string[];

  // ── v2 ──
  v2Step: V2Step;
  ingestionItems: Record<string, IngestionItem>;
  parcours: string | null;
  /** Plaque saisie en étape 1, avant tout upload de pièce. */
  regPlate: string;
  /** Département à 2 ou 3 chiffres (DOM-TOM). */
  department: string;
  /**
   * Déclaré par l'agence à l'étape 1 : le dossier est une succession (décès du
   * titulaire). Force l'ajout des blocs « Acte de décès » + « Justificatif de
   * succession » dans la checklist (cf. buildEffectiveDocQueue).
   */
  isSuccession: boolean;
  /**
   * Déclaré par l'agence à l'étape 1 : le véhicule a été racheté par un
   * professionnel (Parcours A). Force l'ajout du bloc « Récépissé de
   * déclaration d'achat » dans la checklist.
   */
  isProfessionalResale: boolean;
  /**
   * Nature imposée à la main pour une partie, quand la lecture des pièces se
   * trompe (retour François 2026-09-18 : aucune bascule possible depuis CGIA).
   * Absent = la règle `natureVendeur` / `natureAcheteur` décide seule.
   */
  entityKindOverrides: EntityKindOverrides;
  /** Snapshot ECG Auto récupéré en étape 1 → utilisé en étape 2 (tax confirm) et au moment du POST /api/cg. */
  taxData: TaxDataSnapshot | null;
  /**
   * Destination après validation de l'écran de confirmation du tarif (étape 2).
   *  - `'ingesting'` : flux normal (étape 1 → 2 → dépôt des pièces).
   *  - `'final'` : retour depuis l'étape de vérification après recalcul ECG Auto
   *    (plaque/département modifiés) — la validation du nouveau tarif enchaîne
   *    sur la certification puis la génération de facture.
   */
  taxConfirmReturn: 'ingesting' | 'final';
  /** Id de la row cg_in_flight_uploads que cette session pilote (= source de vérité backend). */
  activeInFlightId: string | null;
  // ── Mode édition (chip "Pièces à déposer" → /form-cgia?registrationId=X) ──
  /** RegistrationID si on est en mode édition (redepôt), null en mode nouveau dossier. */
  editingRegistrationId: number | null;
  /** Demandes de redepôt ouvertes — vide en mode nouveau dossier. */
  openDocumentRequests: OpenDocumentRequest[];
  /** Liste des docTypes déjà validés par GOD (lecture seule en mode édition). */
  validatedLockedDocTypes: CgDocType[];
  /** Statut cible après fulfill (One-time → awaiting_payment, sinon → pending_god_review). */
  nextStatusOnFulfill: 'awaiting_payment' | 'pending_god_review' | null;
  /**
   * Photo figée du résultat d'analyse EXACT affiché par FormCGIA, mise à jour
   * en continu et envoyée avec le POST /api/cg à la soumission. La page review
   * la rejoue verbatim → analyse iso garantie entre /form-cgia et /cg/:id/review
   * (plus de re-dérivation regex divergente côté review).
   */
  analysisSnapshot: AnalysisSnapshot | null;

  // Actions legacy (conservées pour ne pas casser les imports, inusitées par v2)
  startWizard: () => void;
  setCurrentStep: (step: WizardStep) => void;
  setDocQueue: (queue: CgDocType[]) => void;
  recordDocResult: (result: UploadedDocResult) => void;
  /** Rattache manuellement une pièce d'identité à une personne (verso indécidable). */
  reassignIdentityPerson: (imageUrl: string, person: IdentityPerson) => void;
  setRejection: (reason: string | null) => void;
  advanceToNextDoc: () => void;
  jumpToReuploadDoc: (docType: CgDocType) => void;
  setHebergementUploads: (uploads: HebergementUploads) => void;
  setKbisUrls: (urls: string[]) => void;
  setCoBuyerFiles: (urls: string[]) => void;
  setAgencyComment: (comment: string) => void;
  setAgencyCommentFiles: (urls: string[]) => void;
  reset: () => void;

  // Actions v2
  startV2: () => void;
  setV2Step: (step: V2Step) => void;
  enqueueIngestionItems: (items: IngestionItem[]) => void;
  updateIngestionItem: (id: string, patch: Partial<IngestionItem>) => void;
  removeIngestionItem: (id: string) => void;
  setParcours: (parcours: string | null) => void;
  setAnalysisSnapshot: (snapshot: AnalysisSnapshot | null) => void;
  setPlateAndDepartment: (regPlate: string, department: string) => void;
  setIsSuccession: (value: boolean) => void;
  setIsProfessionalResale: (value: boolean) => void;
  setEntityKindOverride: (partie: 'vendeur' | 'acheteur', nature: NatureChoisie | null) => void;
  setTaxData: (taxData: TaxDataSnapshot | null) => void;
  setTaxConfirmReturn: (target: 'ingesting' | 'final') => void;
  /** Met à jour la valeur d'un champ extrait (édition agence à l'étape de vérification). */
  updateExtractedFieldValue: (fieldKey: string, value: string) => void;
  setActiveInFlightId: (id: string | null) => void;
  serializeV2Snapshot: () => V2SessionSnapshot;
  hydrateV2FromSnapshot: (snapshot: V2SessionSnapshot, inFlightId: string) => void;
  hydrateV2FromAgencyEditContext: (ctx: AgencyEditContext) => void;
  resetV2: () => void;
}

export interface HebergementUploads {
  hostProofOfResidence: string[];
  hostIdentity: string[];
  honorAttestation: string[];
  guestProofOfResidence: string[];
}

const EMPTY_HEBERGEMENT: HebergementUploads = {
  hostProofOfResidence: [],
  hostIdentity: [],
  honorAttestation: [],
  guestProofOfResidence: [],
};

const INITIAL_STATE: Omit<FormCGIAState, keyof ActionsOnly> = {
  currentStep: 'intro',
  docQueue: [...DEFAULT_DOCUMENT_QUEUE],
  currentDocIndex: 0,
  uploadedDocs: {},
  extractedFields: {},
  identityDocsPool: [],
  kbisDocsPool: [],
  identityAmbiguousUrls: [],
  rejectionReason: null,
  reuploadMode: null,
  hebergementUploads: { ...EMPTY_HEBERGEMENT },
  kbisUrls: [],
  coBuyerFiles: [],
  agencyComment: '',
  agencyCommentFiles: [],
  v2Step: 'intro',
  ingestionItems: {},
  parcours: null,
  regPlate: '',
  department: '',
  isSuccession: false,
  isProfessionalResale: false,
  entityKindOverrides: {},
  taxData: null,
  taxConfirmReturn: 'ingesting',
  activeInFlightId: null,
  editingRegistrationId: null,
  openDocumentRequests: [],
  validatedLockedDocTypes: [],
  nextStatusOnFulfill: null,
  analysisSnapshot: null,
};

type ActionsOnly = {
  startWizard: () => void;
  setCurrentStep: (step: WizardStep) => void;
  setDocQueue: (queue: CgDocType[]) => void;
  recordDocResult: (result: UploadedDocResult) => void;
  /** Rattache manuellement une pièce d'identité à une personne (verso indécidable). */
  reassignIdentityPerson: (imageUrl: string, person: IdentityPerson) => void;
  setRejection: (reason: string | null) => void;
  advanceToNextDoc: () => void;
  jumpToReuploadDoc: (docType: CgDocType) => void;
  setHebergementUploads: (uploads: HebergementUploads) => void;
  setKbisUrls: (urls: string[]) => void;
  setCoBuyerFiles: (urls: string[]) => void;
  setAgencyComment: (comment: string) => void;
  setAgencyCommentFiles: (urls: string[]) => void;
  reset: () => void;
  startV2: () => void;
  setV2Step: (step: V2Step) => void;
  enqueueIngestionItems: (items: IngestionItem[]) => void;
  updateIngestionItem: (id: string, patch: Partial<IngestionItem>) => void;
  removeIngestionItem: (id: string) => void;
  setParcours: (parcours: string | null) => void;
  setAnalysisSnapshot: (snapshot: AnalysisSnapshot | null) => void;
  setPlateAndDepartment: (regPlate: string, department: string) => void;
  setIsSuccession: (value: boolean) => void;
  setIsProfessionalResale: (value: boolean) => void;
  setEntityKindOverride: (partie: 'vendeur' | 'acheteur', nature: NatureChoisie | null) => void;
  setTaxData: (taxData: TaxDataSnapshot | null) => void;
  setTaxConfirmReturn: (target: 'ingesting' | 'final') => void;
  /** Met à jour la valeur d'un champ extrait (édition agence à l'étape de vérification). */
  updateExtractedFieldValue: (fieldKey: string, value: string) => void;
  setActiveInFlightId: (id: string | null) => void;
  serializeV2Snapshot: () => V2SessionSnapshot;
  hydrateV2FromSnapshot: (snapshot: V2SessionSnapshot, inFlightId: string) => void;
  hydrateV2FromAgencyEditContext: (ctx: AgencyEditContext) => void;
  resetV2: () => void;
};

/**
 * Pondération `docType` pour les champs critiques. La carte grise est la
 * source de vérité absolue sur l'immatriculation et le VIN ; les autres
 * pièces sont moins fiables (l'assurance mentionne souvent juste un fragment
 * de VIN, le mandat est rempli à la main, etc.).
 *
 * Score d'arbitrage = confidence × FIELD_PRIORITY[fieldKey][docType] (défaut 5).
 *
 * Évite les cas pathologiques où un mandat ou une assurance écrase la valeur
 * de la CG à cause d'une confidence légèrement plus haute.
 */
const FIELD_PRIORITY: Record<string, Partial<Record<CgDocType, number>>> = {
  immatriculation: {
    carte_grise: 10,
    cerfa_cession: 9,
    cerfa_demande_immat: 8,
    mandat: 7,
    assurance: 6,
    controle_technique: 6,
  },
  vin: {
    carte_grise: 10,
    cerfa_cession: 9,
    mandat: 8,
    controle_technique: 7,
    cerfa_demande_immat: 6,
    assurance: 4,
  },
  marque: { carte_grise: 10, controle_technique: 6, mandat: 6 },
  date_premiere_mec: { carte_grise: 10, cerfa_demande_immat: 7, controle_technique: 6 },
  numero_formule: { carte_grise: 10, cerfa_demande_immat: 7 },
  // ── Adresse de l'ACQUÉREUR ────────────────────────────────────────────
  // Le justificatif de domicile est un document IMPRIMÉ (facture, attestation
  // d'assurance habitation) ET la source qui FAIT FOI pour l'adresse
  // immatriculée (§5 du référentiel CTVO). Le Cerfa 13750, lui, est rempli à
  // la MAIN : ses chiffres sont la première source d'erreur de lecture.
  //
  // Sans pondération, l'arbitrage se faisait à la seule confiance OCR — donc
  // au hasard entre deux valeurs annoncées à 0.95. Mesuré sur le parc réel
  // (juillet 2026) : 19 dossiers où le code postal manuscrit du Cerfa diffère
  // de celui du justificatif alors que la COMMUNE est identique — 30170 au
  // lieu de 30700 (Blauzac), 29120 au lieu de 29200 (Brest), 92160 au lieu de
  // 92600 (Asnières). À chaque fois c'est le Cerfa qui est faux, et rien ne
  // garantissait que ce ne soit pas lui qui l'emporte et parte à MiSIV.
  code_postal: { justif_domicile: 10, attestation_hebergement: 6, cerfa_demande_immat: 4 },
  ville: { justif_domicile: 10, attestation_hebergement: 6, cerfa_demande_immat: 4 },
  nom_voie: { justif_domicile: 10, attestation_hebergement: 6, cerfa_demande_immat: 4 },
  type_voie: { justif_domicile: 10, attestation_hebergement: 6, cerfa_demande_immat: 4 },
  numero_voie: { justif_domicile: 10, attestation_hebergement: 6, cerfa_demande_immat: 4 },
  complement_adresse: { justif_domicile: 10, attestation_hebergement: 6, cerfa_demande_immat: 4 },
  // Vendeur = titulaire actuel : information réglementaire de la carte grise.
  titulaire_civilite: { carte_grise: 10 },
  titulaire_nom: { carte_grise: 10 },
  titulaire_prenom: { carte_grise: 10 },
  titulaire_nom_usage: { carte_grise: 10 },
  titulaire_siren: { carte_grise: 10 },
  // Identité du CO-TITULAIRE acquéreur : sa pièce d'identité FAIT FOI (méthodo
  // François — « aller chercher les informations sur la pièce d'identité »).
  // Ses champs sont produits par le remap cotitulaire_* du routage d'identité
  // (cf. integrateDocResult) et priment sur la lecture du Cerfa 13750.
  cotitulaire_nom: { piece_identite_cotitulaire: 8, permis_conduire_cotitulaire: 7 },
  cotitulaire_prenom: { piece_identite_cotitulaire: 8, permis_conduire_cotitulaire: 7 },
  cotitulaire_nom_usage: { piece_identite_cotitulaire: 8, permis_conduire_cotitulaire: 7 },
  cotitulaire_civilite: { piece_identite_cotitulaire: 8, permis_conduire_cotitulaire: 7 },
};

const DEFAULT_PRIORITY = 5;

/**
 * Sources DISQUALIFIÉES par champ — collision sémantique entre docTypes
 * (miroir de EXCLUDED_FIELD_SOURCES côté cgReviewController) :
 *
 *  - Cerfa 13750 : `titulaire_*` = NOUVEAU titulaire (l'acheteur) ; sur la
 *    carte grise = titulaire ACTUEL (le vendeur). Sans exclusion, le nom de
 *    l'acheteur pouvait pré-remplir les champs Vendeur.
 *  - `cotitulaire_*` alimente le co-titulaire de la NOUVELLE immatriculation
 *    (cobuyer) : la valeur carte grise (ancien co-titulaire) est hors sujet.
 */
/**
 * Slots d'identité qui n'appartiennent PAS au titulaire acquéreur : leurs
 * champs d'identité génériques (nom/prenom/…) ne doivent JAMAIS alimenter
 * l'identité du dossier. Les faces VERSO sont incluses (même personne). Le
 * titulaire garde ses 4 slots (CNI + permis, recto + verso) comme sources
 * légitimes — le verso porte l'adresse et la date de délivrance.
 */
const NON_TITULAIRE_IDENTITY_SLOTS: readonly CgDocType[] = [
  'piece_identite_hebergeur',
  'piece_identite_hebergeur_verso',
  'piece_identite_cotitulaire',
  'piece_identite_cotitulaire_verso',
  'permis_conduire_cotitulaire',
  'permis_conduire_cotitulaire_verso',
];

/**
 * ⚠️ GARDE CRITIQUE — le Kbis du VENDEUR ne doit pas alimenter l'ACHETEUR.
 *
 * Depuis le dédoublement du slot, un dossier peut porter deux Kbis. Les deux
 * rendent les MÊMES clés génériques (`raison_sociale`, `siret`, `siren`), et ces
 * clés sont câblées sur l'acheteur dans `FORM_TO_OCR_KEYS`. La fusion ne gardant
 * qu'un gagnant par clé, au score confiance × priorité et sans regarder de quel
 * côté vient la pièce, le Kbis du vendeur pouvait rafler le slot de l'acheteur.
 *
 * GR-556-WF (retour François 2026-09-21) : la raison sociale est partie du Kbis
 * VENDEUR (0.88) et le SIRET du Kbis ACQUÉREUR (0.97). L'acheteur réel, MAROT
 * GUILLAUME, s'est retrouvé enregistré « CEVEN EXPRESS -CEVENNES TERROIR » avec
 * son propre SIRET — la raison sociale de son vendeur, sur son identifiant à lui.
 * L'OCR avait pourtant lu les deux Kbis correctement.
 *
 * On renomme donc ces clés vers leurs équivalents vendeur au moment de la fusion,
 * plutôt que de les jeter : elles cessent de polluer l'acheteur ET renseignent
 * enfin le vendeur, qui jusqu'ici ne tenait sa raison sociale que de la cession.
 */
const KBIS_VENDEUR_FIELD_REMAP: Record<string, string> = {
  raison_sociale: 'vendeur_raison_sociale',
  siret: 'vendeur_siret',
  siren: 'vendeur_siren',
};

const EXCLUDED_FIELD_SOURCES: Record<string, readonly CgDocType[]> = {
  titulaire_civilite: ['cerfa_demande_immat'],
  titulaire_nom: ['cerfa_demande_immat'],
  titulaire_prenom: ['cerfa_demande_immat'],
  titulaire_nom_usage: ['cerfa_demande_immat'],
  // Le co-titulaire du dossier CTVO est le co-titulaire ACQUÉREUR (nouvelle
  // immatriculation, cerfa 13750) — PAS le C.4.1 de la carte grise, qui est le
  // co-titulaire VENDEUR (traité à part pour la signature de la cession). On
  // exclut donc la carte grise de TOUS les champs cotitulaire_*, y compris le
  // booléen `cotitulaire_present` : sans ça, un C.4.1 vendeur pré-cochait à tort
  // le co-titulaire acheteur et créait un CoBuyer erroné (retour François
  // DY-565-KJ : CARCENAC en acheteur ET cotitulaire).
  cotitulaire_present: ['carte_grise'],
  cotitulaire_civilite: ['carte_grise'],
  cotitulaire_nom: ['carte_grise'],
  cotitulaire_prenom: ['carte_grise'],
  cotitulaire_nom_usage: ['carte_grise'],
  // ⚠️ GARDE CRITIQUE — les pièces d'identité qui ne sont PAS celles de
  // l'acquéreur ne doivent JAMAIS alimenter les champs d'identité du dossier.
  // `piece_identite_cotitulaire` rejoint `piece_identite_hebergeur` dans cette
  // liste dès sa création : le co-titulaire est une AUTRE personne que
  // l'acquéreur, et son nom/prénom/date de naissance partirait sinon à l'ANTS
  // à la place de celui du titulaire. C'est exactement la classe de bug déjà
  // signalée par le pôle CG sur CY-517-VW (« tu as rattaché la pièce
  // d'identité de Madame Carolina FATATI pour l'acheteur ») et DM-724-VQ
  // (« tu as mis la pièce d'identité de l'hébergeur HULIN MAGALIE »).
  // Ses champs restent lisibles sous le préfixe cotitulaire_* via FIELD_PRIORITY.
  //
  // La 2e pièce d'identité d'un dossier (CNI du cotitulaire OU de l'hébergeur,
  // rangée en `piece_identite_hebergeur` par l'overflow anti-écrasement de
  // useAutoExtractQueue) NE DOIT PAS écraser l'identité de l'ACQUÉREUR : ce
  // n'est PAS la même personne. Sans cette garde, la CNI du cotitulaire (ou de
  // l'hébergeur) remplaçait nom/prénom/civilité/naissance de l'acheteur
  // (cas FZ-361-QS : fiche « Acheteur » affichée HASSAB au lieu d'ORAKCI, avec
  // la pastille source « hébergeur » trompeuse). L'identité de l'acquéreur
  // reste portée par sa propre CNI + son permis.
  nom: NON_TITULAIRE_IDENTITY_SLOTS,
  prenom: NON_TITULAIRE_IDENTITY_SLOTS,
  nom_usage: NON_TITULAIRE_IDENTITY_SLOTS,
  civilite: NON_TITULAIRE_IDENTITY_SLOTS,
  date_naissance: NON_TITULAIRE_IDENTITY_SLOTS,
  lieu_naissance: NON_TITULAIRE_IDENTITY_SLOTS,
  departement_naissance: NON_TITULAIRE_IDENTITY_SLOTS,
  pays_naissance: NON_TITULAIRE_IDENTITY_SLOTS,
  numero_document: NON_TITULAIRE_IDENTITY_SLOTS,
  type_document: NON_TITULAIRE_IDENTITY_SLOTS,
  adresse: NON_TITULAIRE_IDENTITY_SLOTS,
  // Validité de la pièce : celle d'un TIERS ne doit pas devenir celle du
  // titulaire. Sur EA-035-SV (reg 11910), la CNI d'une autre personne a écrasé
  // la date d'expiration du permis du titulaire (2040-01-23 → 2032-03-20) —
  // valeur fausse envoyée à l'ANTS, et la règle `piece_identite_expiree`
  // jugeait sur la mauvaise date (retour Luc 2026-07-30).
  date_expiration: NON_TITULAIRE_IDENTITY_SLOTS,
  date_delivrance: NON_TITULAIRE_IDENTITY_SLOTS,
};

const isExcludedFieldSource = (fieldKey: string, docType: CgDocType): boolean =>
  (EXCLUDED_FIELD_SOURCES[fieldKey] ?? []).includes(docType);

const computeMergeScore = (entry: ExtractedFieldEntry): number => {
  const priority = FIELD_PRIORITY[entry.fieldKey]?.[entry.sourceDocType] ?? DEFAULT_PRIORITY;
  return entry.field.confidence * priority;
};

/**
 * Une carte grise « recto » porte l'identification du véhicule (VIN, marque,
 * genre, n° de formule). Le « verso » scanné séparément ne montre souvent que
 * le COUPON DE CESSION rempli : son champ `titulaire_*` porte alors l'ACHETEUR
 * (nouveau propriétaire), PAS le titulaire C.1. Sans cette garde, le verso
 * écrase le recto dans le slot `carte_grise` et `coherence_vendeur_cg_cession`
 * compare le vendeur au mauvais nom (faux « vente après décès » — cas DE-107-LA :
 * titulaire C.1 réel = TIDDA, verso lu = l'acheteur DEBONO).
 */
const cgHasVehicleIdentity = (doc?: UploadedDocResult): boolean => {
  const fields = doc?.ocrResponse?.fields as Record<string, { value?: unknown }> | undefined;
  if (!fields) return false;
  const filled = (key: string): boolean => {
    const v = fields[key]?.value;
    return typeof v === 'string' ? v.trim().length > 0 : v != null;
  };
  return filled('vin') || filled('marque') || filled('genre') || filled('numero_formule');
};

/**
 * Face PRINCIPALE d'une carte grise : celle qui identifie le VÉHICULE.
 *
 * À distinguer de `cgHasVehicleIdentity`, qui dit seulement « cette face
 * apporte quelque chose du véhicule » — le seul n° de formule suffit à la
 * satisfaire. C'est trop laxiste pour arbitrer un remplacement : sur
 * GL-911-WF, le coupon détachable (n° de formule, aucun champ véhicule, et le
 * Cerfa 13750 dans le même fichier) a REMPLACÉ la face principale dans le slot.
 * Le titulaire lu est alors devenu celui du 13750 — l'ACQUÉREUR, collision de
 * clés décrite au §12 du référentiel — d'où la fausse alerte « le vendeur de la
 * cession n'est pas le titulaire de la carte grise (CHAZAL) » sur un dossier où
 * la carte grise porte bien MANGONI.
 */
const cgHasMainFace = (doc?: UploadedDocResult): boolean => {
  const fields = doc?.ocrResponse?.fields as Record<string, { value?: unknown }> | undefined;
  if (!fields) return false;
  const filled = (key: string): boolean => {
    const v = fields[key]?.value;
    return typeof v === 'string' ? v.trim().length > 0 : v != null;
  };
  return filled('immatriculation') || filled('vin');
};

/**
 * Fusionne deux faces d'une même carte grise : la face principale sert de base,
 * l'autre ne comble que les TROUS.
 *
 * Sans ça, garder la face principale perdrait ce que le coupon détachable est
 * justement seul à porter (le n° de formule, cf. `numero_formule_coupon_detache`),
 * et la remplacer perdrait l'identification du véhicule. Aucune valeur déjà lue
 * n'est écrasée : c'est ce qui empêche le titulaire du 13750, glissé dans un
 * fichier multi-pièces, de recouvrir celui de la carte grise.
 */
const mergeCarteGriseFaces = (base: UploadedDocResult, complement: UploadedDocResult): UploadedDocResult => {
  const baseFields = (base.ocrResponse.fields ?? {}) as Record<string, { value?: unknown }>;
  const extraFields = (complement.ocrResponse.fields ?? {}) as Record<string, { value?: unknown }>;
  const isEmpty = (v: unknown): boolean => v === null || v === undefined || (typeof v === 'string' && !v.trim());

  const merged = { ...baseFields };
  Object.entries(extraFields).forEach(([key, field]) => {
    if (isEmpty(field?.value)) return;
    if (!isEmpty(merged[key]?.value)) return;
    merged[key] = field;
  });

  return {
    ...base,
    firebaseUrls: [...new Set([...base.firebaseUrls, ...complement.firebaseUrls])],
    ocrResponse: { ...base.ocrResponse, fields: merged as UploadedDocResult['ocrResponse']['fields'] },
  };
};

const ctFieldStr = (doc: UploadedDocResult | undefined, key: string): string | null => {
  const v = (doc?.ocrResponse?.fields as Record<string, { value?: unknown }> | undefined)?.[key]?.value;
  return typeof v === 'string' && v.trim().length > 0 ? v : null;
};

const ctDateMs = (doc: UploadedDocResult | undefined): number | null => {
  const raw = ctFieldStr(doc, 'date_ct');
  if (!raw) return null;
  const ms = new Date(raw).getTime();
  return Number.isNaN(ms) ? null : ms;
};

/**
 * Arbitrage du slot `controle_technique` quand DEUX fichiers CT (CT initial +
 * contre-visite) atterrissent dans le même slot `uploadedDocs.controle_technique`.
 *
 * Sans arbitrage, le dernier OCR terminé gagnait le slot — or l'OCR tourne en
 * parallèle (max 5 analyses), donc le slot était NON DÉTERMINISTE : selon la
 * course, `ct_validite` lisait soit la contre-visite favorable, soit le CT
 * défavorable, et bloquait aléatoirement le dossier (cas DS-513-PJ : défavorable
 * majeur 17/03 + contre-visite favorable 08/04, verdict pile/face entre runs).
 *
 * Le CT qui fait foi est la contre-visite : on garde la `date_ct` la PLUS
 * RÉCENTE (la contre-visite est toujours postérieure au CT initial), conforme à
 * la doctrine « contre-visite favorable → repasse sous la règle des 6 mois »
 * (skill ctvo-verificateur §13.6). À dates égales ou illisibles, un résultat
 * `favorable` prime sur un non-favorable. Pur → testable directement (le hook
 * d'ingestion `useAutoExtractQueue` ne l'est pas en env `node`).
 *
 * @returns true s'il faut CONSERVER l'existant (l'entrant ne le supplante pas).
 */
export const ctSlotKeepsExisting = (existing: UploadedDocResult | undefined, incoming: UploadedDocResult): boolean => {
  if (incoming.docType !== 'controle_technique' || existing?.docType !== 'controle_technique') {
    return false;
  }
  const tExisting = ctDateMs(existing);
  const tIncoming = ctDateMs(incoming);
  if (tExisting !== null && tIncoming !== null && tExisting !== tIncoming) {
    return tExisting > tIncoming;
  }
  const favExisting = ctFieldStr(existing, 'resultat') === 'favorable';
  const favIncoming = ctFieldStr(incoming, 'resultat') === 'favorable';
  if (favExisting !== favIncoming) return favExisting;
  return false;
};

/**
 * Fusionne les nouveaux champs extraits dans `extractedFields` en gardant la
 * meilleure source. Pour les champs critiques (immatriculation, vin) la
 * pondération par docType l'emporte. Priorité au champ existant en cas
 * d'égalité.
 */
export const mergeExtractedFields = (
  current: Record<string, ExtractedFieldEntry>,
  newResponse: CgOcrResponse,
  sourceDocType: CgDocType,
  options: { trace?: boolean } = {},
): Record<string, ExtractedFieldEntry> => {
  const merged = { ...current };
  Object.entries(newResponse.fields).forEach(([rawFieldKey, field]) => {
    if (field.source === 'not_found' || field.value === null) return;

    // Les clés génériques du Kbis VENDEUR sont réaffectées au vendeur avant toute
    // comparaison : sans ça elles concourent pour le slot de l'acheteur (cf.
    // KBIS_VENDEUR_FIELD_REMAP).
    const remapped = sourceDocType === 'kbis_vendeur' ? KBIS_VENDEUR_FIELD_REMAP[rawFieldKey] : undefined;
    const fieldKey = remapped ?? rawFieldKey;
    if (remapped && options.trace) {
      traceIa(
        'FUSION',
        `Champ "${rawFieldKey}" lu sur "kbis_vendeur" → renommé "${remapped}" : cette clé alimente l'ACHETEUR, ` +
          `le Kbis du vendeur ne doit pas la lui prendre.`,
      );
    }

    if (isExcludedFieldSource(fieldKey, sourceDocType)) {
      if (options.trace) {
        traceIa(
          'FUSION',
          `Champ "${fieldKey}" lu sur "${sourceDocType}" → IGNORÉ : source disqualifiée pour ce champ ` +
            `(collision sémantique titulaire/cotitulaire entre carte grise et Cerfa 13750 — la carte grise fait foi pour le vendeur).`,
        );
      }
      return;
    }

    const newEntry: ExtractedFieldEntry = { fieldKey, field, sourceDocType };
    const existing = merged[fieldKey];
    const newScore = computeMergeScore(newEntry);
    const shouldReplace = !existing || newScore > computeMergeScore(existing);

    if (options.trace) {
      if (!existing) {
        traceIa(
          'FUSION',
          `Champ "${fieldKey}" ← ${JSON.stringify(field.value)} retenu depuis "${sourceDocType}" ` +
            `(score ${newScore.toFixed(2)} = confiance ${Math.round(field.confidence * 100)}% × priorité du type de pièce).`,
        );
      } else if (shouldReplace) {
        traceIa(
          'FUSION',
          `Champ "${fieldKey}" REMPLACÉ : "${sourceDocType}" (score ${newScore.toFixed(2)}) bat "${existing.sourceDocType}" ` +
            `(score ${computeMergeScore(existing).toFixed(2)}). Nouvelle valeur: ${JSON.stringify(field.value)} ` +
            `(ancienne: ${JSON.stringify(existing.field.value)}).`,
        );
      } else {
        traceIa(
          'FUSION',
          `Champ "${fieldKey}" CONSERVÉ depuis "${existing.sourceDocType}" (score ${computeMergeScore(existing).toFixed(2)} ≥ ` +
            `${newScore.toFixed(2)} pour "${sourceDocType}"). Valeur ${JSON.stringify(field.value)} non retenue.`,
        );
      }
    }

    if (shouldReplace) {
      merged[fieldKey] = newEntry;
    }
  });
  return merged;
};

/** Pièces de référence dont l'arrivée permet d'élire la cession qui fait foi. */
const CESSION_REF_DOC_TYPES: readonly CgDocType[] = ['cerfa_demande_immat', 'carte_grise'];

/**
 * Recalcule quelle cession fait foi, puis reconstruit la part « cession » de
 * extractedFields depuis CETTE cession seule.
 *
 * La purge est indispensable : sans elle, les champs des cessions écartées
 * restent dans l'agrégat (le nom lu sur l'une, la raison sociale sur l'autre)
 * — c'est le mélange qui a fait voir « SKIPCAR » en acquéreur sur GD-733-LW.
 * Les valeurs saisies à la main (confiance 1) survivent, comme au routage des
 * identités.
 */
const applyCessionElection = (
  uploadedDocs: Partial<Record<CgDocType, UploadedDocResult>>,
  extractedFields: Record<string, ExtractedFieldEntry>,
  pool: CessionPoolEntry[],
  options: { trace?: boolean } = {},
): Pick<IntegratedDocState, 'uploadedDocs' | 'extractedFields' | 'cessionDocsPool'> => {
  const election = elireCession(pool, buildCessionRefs(uploadedDocs));

  const nextDocs: Partial<Record<CgDocType, UploadedDocResult>> = { ...uploadedDocs };
  if (election.elue) nextDocs.cerfa_cession = election.elue;

  const rebuilt: Record<string, ExtractedFieldEntry> = {};
  Object.entries(extractedFields).forEach(([key, entry]) => {
    const isManualEdit = entry.field.confidence === 1 && entry.field.source === 'extracted';
    if (entry.sourceDocType !== 'cerfa_cession' || isManualEdit) rebuilt[key] = entry;
  });
  const merged = election.elue
    ? mergeExtractedFields(rebuilt, election.elue.ocrResponse, 'cerfa_cession', options)
    : rebuilt;

  if (options.trace && pool.length > 1) {
    traceIa(
      'CESSION-QUI-FAIT-FOI',
      `${pool.length} certificats de cession au dossier (chaîne de propriété). Retenu par « ${election.critere} » : ` +
        `…${(election.elue?.firebaseUrls[0] ?? '').slice(-24)}. Les ${election.autres.length} autre(s) restent ` +
        `consultables en « Autres pièces reçues » mais n'alimentent ni les champs ni les alertes.`,
    );
  }

  return { uploadedDocs: nextDocs, extractedFields: merged, cessionDocsPool: pool };
};

/** Pièces de référence dont l'arrivée déclenche un re-routage des identités. */
const IDENTITY_REF_DOC_TYPES: readonly CgDocType[] = [
  'cerfa_demande_immat',
  'cerfa_cession',
  'mandat',
  'attestation_hebergement',
];

/** Ordre déterministe de fusion des slots d'identité dans extractedFields. */
const IDENTITY_MERGE_ORDER: readonly CgDocType[] = [
  'piece_identite',
  'piece_identite_verso',
  'permis_conduire',
  'permis_conduire_verso',
  'piece_identite_cotitulaire',
  'piece_identite_cotitulaire_verso',
  'permis_conduire_cotitulaire',
  'permis_conduire_cotitulaire_verso',
  'piece_identite_hebergeur',
  'piece_identite_hebergeur_verso',
];

/**
 * Champs d'identité génériques remappés en `cotitulaire_*` quand la pièce est
 * routée vers un slot CO-TITULAIRE : sa CNI fait foi pour SES champs (méthodo
 * François), qui alimentent le bloc co-titulaire du formulaire au lieu d'être
 * simplement ignorés.
 */
const COTITULAIRE_FIELD_REMAP: Record<string, string> = {
  nom: 'cotitulaire_nom',
  prenom: 'cotitulaire_prenom',
  nom_usage: 'cotitulaire_nom_usage',
  civilite: 'cotitulaire_civilite',
};

const remapResponseForSlot = (slot: CgDocType, response: CgOcrResponse): CgOcrResponse => {
  if (identityPersonOf(slot) !== 'cotitulaire') return response;
  const fields: Record<string, CgOcrField> = { ...response.fields };
  Object.entries(COTITULAIRE_FIELD_REMAP).forEach(([from, to]) => {
    const field = response.fields[from];
    if (field && field.value !== null && field.source !== 'not_found' && !fields[to]) {
      fields[to] = field;
    }
  });
  return { ...response, fields };
};

export interface IntegratedDocState {
  uploadedDocs: Partial<Record<CgDocType, UploadedDocResult>>;
  extractedFields: Record<string, ExtractedFieldEntry>;
  identityDocsPool: IdentityPoolEntry[];
  kbisDocsPool: KbisPoolEntry[];
  /**
   * Tous les certificats de cession déposés. Un dossier de revente en porte
   * plusieurs (chaîne de propriété) alors que le formulaire n'a qu'UN slot :
   * `elireCession` désigne celui qui fait foi, cf. v2/cessionRouting.
   */
  cessionDocsPool: CessionPoolEntry[];
  /** Slot assigné par URL de fichier après routage (vide si aucun re-routage). */
  identitySlotByUrl: Record<string, CgDocType>;
  /**
   * Slot assigné par pièce du pool — clé `identityPoolKey(url, nature)`. Seul
   * ce niveau distingue les deux pièces d'un scan multi-pièces (CNI + permis
   * partagent leur URL) ; c'est donc lui qui fait foi pour la persistance.
   */
  identitySlotByPoolKey: Record<string, CgDocType>;
  /** Pièces assignées sans preuve de personne (cf. IdentityRoutingResult). */
  identityAmbiguousUrls: string[];
}

/**
 * Recalcule les slots d'identité depuis le pool puis reconstruit la part
 * « identité » de extractedFields. Les entrées éditées à la main (confiance 1,
 * saisie humaine via updateExtractedFieldValue) sont préservées.
 */
const applyIdentityRouting = (
  uploadedDocs: Partial<Record<CgDocType, UploadedDocResult>>,
  extractedFields: Record<string, ExtractedFieldEntry>,
  pool: IdentityPoolEntry[],
  options: { trace?: boolean } = {},
): IntegratedDocState => {
  const routing = routeIdentityDocs(pool, uploadedDocs);

  const nextDocs: Partial<Record<CgDocType, UploadedDocResult>> = {};
  (Object.entries(uploadedDocs) as Array<[CgDocType, UploadedDocResult]>).forEach(([docType, doc]) => {
    if (!isIdentitySlotType(docType)) nextDocs[docType] = doc;
  });

  // Filet : un slot d'identité dont AUCUNE url n'est connue du pool n'est pas
  // « non routé », il est HORS PÉRIMÈTRE de ce routage — le pool ne peut donc
  // pas se prononcer sur lui. Repartir de zéro sur toute la famille identité
  // (comportement d'origine) l'effaçait silencieusement dès que le pool était
  // incomplet : c'est ce qui se produisait sur un dossier repris, dont le pool
  // n'était pas transporté par la bascule. On les conserve ; le routage reste
  // prioritaire s'il a une opinion sur le même slot.
  const pooledUrls = new Set(pool.flatMap((entry) => entry.doc.firebaseUrls));
  const preservedSlots: Array<[CgDocType, UploadedDocResult]> = (
    Object.entries(uploadedDocs) as Array<[CgDocType, UploadedDocResult]>
  ).filter(
    ([docType, doc]) =>
      isIdentitySlotType(docType) &&
      !routing.slots[docType] &&
      !doc.firebaseUrls.some((url) => pooledUrls.has(url)),
  );
  preservedSlots.forEach(([docType, doc]) => {
    nextDocs[docType] = doc;
  });
  Object.assign(nextDocs, routing.slots);

  // Les champs extraits d'un slot préservé doivent survivre à la purge, sinon
  // la pièce reste dans la checklist mais le formulaire se vide de son identité.
  const preservedSlotTypes = new Set<CgDocType>(preservedSlots.map(([docType]) => docType));

  const rebuilt: Record<string, ExtractedFieldEntry> = {};
  Object.entries(extractedFields).forEach(([key, entry]) => {
    const isManualEdit = entry.field.confidence === 1 && entry.field.source === 'extracted';
    if (
      !isIdentitySlotType(entry.sourceDocType) ||
      isManualEdit ||
      preservedSlotTypes.has(entry.sourceDocType)
    ) {
      rebuilt[key] = entry;
    }
  });
  let merged = rebuilt;
  IDENTITY_MERGE_ORDER.forEach((slot) => {
    const doc = routing.slots[slot];
    if (!doc) return;
    merged = mergeExtractedFields(merged, remapResponseForSlot(slot, doc.ocrResponse), slot, options);
  });

  if (options.trace) {
    const summary = Object.entries(routing.slotByUrl)
      .map(([url, slot]) => `${slot} ← …${url.slice(-24)}`)
      .join(' ; ');
    traceIa(
      'ROUTAGE-IDENTITE',
      `Routage (personne × type × face) recalculé sur ${pool.length} pièce(s) d'identité : ${summary || 'aucune pièce assignée'}.`,
    );
  }

  return {
    uploadedDocs: nextDocs,
    extractedFields: merged,
    identityDocsPool: pool,
    identitySlotByUrl: routing.slotByUrl,
    identitySlotByPoolKey: routing.slotByPoolKey,
    identityAmbiguousUrls: routing.ambiguousUrls,
  };
};

/** Pièces de référence dont l'arrivée permet enfin d'attribuer un Kbis. */
const KBIS_REF_DOC_TYPES: readonly CgDocType[] = ['cerfa_cession', 'carte_grise'];

/**
 * Champs qui identifient chaque partie en propre, lus dans l'agrégat courant.
 * Le Kbis se rattache à celle dont il porte le SIREN — jamais par convention.
 *
 * Une valeur fournie par un Kbis n'est JAMAIS une référence : le Kbis se
 * prouverait par ses propres données. EJ-509-YJ (2026-10-27) : le second Kbis de
 * l'acquéreur, rangé côté vendeur par repli, avait écrit son SIRET dans
 * `vendeur_siret` (le Kbis l'emporte sur la cession à la fusion) ; à l'arrivée de
 * la cession, le routage y relisait ce SIRET et confirmait le Kbis… côté vendeur.
 * On remonte alors aux pièces de référence elles-mêmes (cession, carte grise).
 */
const kbisRefsFrom = (
  extractedFields: Record<string, ExtractedFieldEntry>,
  uploadedDocs: Partial<Record<CgDocType, UploadedDocResult>> = {},
): KbisRefFields => {
  const valeur = (key: string): unknown => {
    const entry = extractedFields[key];
    if (entry && !isKbisDocType(entry.sourceDocType)) return entry.field.value;
    for (const type of KBIS_REF_DOC_TYPES) {
      const lue = (uploadedDocs[type]?.ocrResponse?.fields as Record<string, { value?: unknown }> | undefined)?.[key]
        ?.value;
      if (lue !== undefined && lue !== null && lue !== '') return lue;
    }
    return undefined;
  };
  return {
    vendeurSiret: valeur('vendeur_siret'),
    vendeurRaisonSociale: valeur('vendeur_raison_sociale'),
    titulaireSiren: valeur('titulaire_siren'),
    acheteurSiret: valeur('acheteur_siret'),
    acheteurRaisonSociale: valeur('acheteur_raison_sociale'),
    acheteurEstPersonneMorale: valeur('acheteur_est_personne_morale'),
    vendeurEstPersonneMorale: valeur('vendeur_est_personne_morale'),
  };
};

/**
 * Recalcule les slots Kbis depuis le pool. Les anciens slots sont purgés avant
 * réécriture : une pièce délogée ne doit pas rester visible dans un slot qu'elle
 * n'occupe plus.
 */
const applyKbisRouting = (
  uploadedDocs: Partial<Record<CgDocType, UploadedDocResult>>,
  extractedFields: Record<string, ExtractedFieldEntry>,
  pool: KbisPoolEntry[],
  options: { trace?: boolean } = {},
): Pick<
  IntegratedDocState,
  | 'uploadedDocs'
  | 'extractedFields'
  | 'kbisDocsPool'
  | 'identitySlotByUrl'
  | 'identitySlotByPoolKey'
  | 'identityAmbiguousUrls'
> => {
  const routing = routeKbisDocs(pool, kbisRefsFrom(extractedFields, uploadedDocs));
  const next: Partial<Record<CgDocType, UploadedDocResult>> = { ...uploadedDocs };
  delete next.kbis_vendeur;
  delete next.kbis_acheteur;
  Object.entries(routing.slots).forEach(([slot, doc]) => {
    next[slot as CgDocType] = doc;
  });

  // Les champs des Kbis sont reconstruits DEPUIS LES SLOTS ROUTÉS, comme le fait
  // déjà le routage des pièces d'identité. Un Kbis est fusionné à son arrivée sous
  // son docType de DÉPÔT (« kbis » tant que la zone dédiée n'a pas été utilisée),
  // c'est-à-dire sans le côté auquel il appartient — le routage ne survient
  // qu'ensuite. Sans cette reconstruction, `KBIS_VENDEUR_FIELD_REMAP` ne voit
  // jamais passer un `kbis_vendeur` et le Kbis du vendeur continue de concourir
  // pour les champs de l'acheteur (GR-556-WF).
  const routedUrls = new Set(Object.values(routing.slots).flatMap((doc) => doc.firebaseUrls));
  const rebuilt: Record<string, ExtractedFieldEntry> = {};
  Object.entries(extractedFields).forEach(([key, entry]) => {
    const isManualEdit = entry.field.confidence === 1 && entry.field.source === 'extracted';
    if (!isKbisDocType(entry.sourceDocType) || isManualEdit) rebuilt[key] = entry;
  });
  let mergedFields = rebuilt;
  (['kbis_acheteur', 'kbis_vendeur'] as const).forEach((slot) => {
    const doc = routing.slots[slot];
    if (doc) mergedFields = mergeExtractedFields(mergedFields, doc.ocrResponse, slot, options);
  });
  // Un Kbis que le routage n'a pas su attribuer garde le comportement historique
  // (rattaché à l'acquéreur par convention) : on ne le purge pas, sinon un dossier
  // ambigu perdrait des champs qu'il affichait jusqu'ici.
  pool
    .filter((entry) => !entry.doc.firebaseUrls.some((url) => routedUrls.has(url)))
    .forEach((entry) => {
      mergedFields = mergeExtractedFields(mergedFields, entry.doc.ocrResponse, 'kbis', options);
    });

  return {
    uploadedDocs: next,
    extractedFields: mergedFields,
    kbisDocsPool: pool,
    identitySlotByUrl: routing.slotByUrl,
    // Un Kbis n'est jamais partagé avec une autre pièce dans un même fichier :
    // l'URL suffit à l'identifier, pas de clé de pool à produire.
    identitySlotByPoolKey: {},
    identityAmbiguousUrls: routing.ambiguousUrls,
  };
};

/**
 * Intègre un résultat OCR dans (uploadedDocs, extractedFields, identityDocsPool).
 * Fonction PURE partagée par le store (recordDocResult) et le snapshot de
 * session du hook d'ingestion — les deux restent iso par construction.
 *
 *  - Pièce d'identité (CNI / permis) : entre dans le pool (clé = URL Firebase),
 *    puis TOUS les slots d'identité sont re-routés (personne × type × face).
 *    Un docType « rôle » (zone de dépôt dédiée co-titulaire / hébergeur) épingle
 *    la personne — la provenance du dépôt prime sur tout matching.
 *  - Pièce de référence (13750, cession, mandat, attestation) : enregistrée
 *    normalement PUIS re-routage du pool (les rôles viennent d'arriver).
 *  - Placeholder multi-doc (champs vides, pièce « contenue dans » un autre
 *    fichier) : pose directe dans le slot de base si libre — pas de routage
 *    possible sans champs.
 *  - Autres pièces : comportement historique (gardes carte grise / CT).
 */
const integrateDocResultSansCession = (
  prev: Pick<IntegratedDocState, 'uploadedDocs' | 'extractedFields' | 'identityDocsPool'> &
    Partial<Pick<IntegratedDocState, 'kbisDocsPool' | 'cessionDocsPool'>>,
  result: UploadedDocResult,
  options: { trace?: boolean } = {},
): Omit<IntegratedDocState, 'cessionDocsPool'> => {
  const kind = identityKindOf(result.docType);
  const isPlaceholder = Object.keys(result.ocrResponse.fields).length === 0;
  const kbisPool = prev.kbisDocsPool ?? [];

  // Extrait Kbis : entre dans son pool, puis les DEUX slots sont re-routés. Le
  // type de dépôt (`kbis_vendeur` / `kbis_acheteur`, zone dédiée) épingle la
  // partie ; sinon elle se déduit des identifiants (cf. v2/kbisRouting).
  if (isKbisDocType(result.docType) && !isPlaceholder) {
    const url = result.firebaseUrls[0] ?? `${result.docType}-${kbisPool.length}`;
    const pinned = result.docType === 'kbis_vendeur' ? 'vendeur' : result.docType === 'kbis_acheteur' ? 'acheteur' : undefined;
    const pool: KbisPoolEntry[] = [
      ...kbisPool.filter((e) => (e.doc.firebaseUrls[0] ?? '') !== url),
      { doc: { ...result }, ...(pinned ? { pinnedPartie: pinned } : {}) },
    ];
    const extractedFields = mergeExtractedFields(prev.extractedFields, result.ocrResponse, result.docType, options);
    return {
      // `extractedFields` n'est PAS réinjecté ici : applyKbisRouting renvoie sa
      // propre version, reconstruite depuis les slots routés.
      ...applyKbisRouting(prev.uploadedDocs, extractedFields, pool, options),
      identityDocsPool: prev.identityDocsPool,
    };
  }

  if (kind && !isPlaceholder) {
    const url = result.firebaseUrls[0] ?? `${result.docType}-${prev.identityDocsPool.length}`;
    const person = identityPersonOf(result.docType);
    const entry: IdentityPoolEntry = {
      doc: { ...result },
      ...(person !== 'titulaire' ? { pinnedPerson: person } : {}),
    };
    // Éviction par (URL × nature) et non par URL seule : un fichier multi-pièces
    // (CNI + permis dans le même scan, `additionalDocs` du backend) partage son
    // URL entre ses pièces — l'éviction par URL seule faisait disparaître la CNI
    // du pool à l'arrivée du permis contenu. Même URL + même nature = ré-analyse
    // du même fichier → remplacement.
    const pool = [
      ...prev.identityDocsPool.filter(
        (e) => (e.doc.firebaseUrls[0] ?? '') !== url || identityKindOf(e.doc.docType) !== kind,
      ),
      entry,
    ];
    return { ...applyIdentityRouting(prev.uploadedDocs, prev.extractedFields, pool, options), kbisDocsPool: kbisPool };
  }

  // Placeholder d'identité (pièce signalée dans un fichier multi-doc, champs
  // vides) : matérialise le slot de base s'il est libre pour que la checklist
  // ne la réclame plus — sans polluer le pool (rien à router sans champs).
  if (kind && isPlaceholder) {
    if (prev.uploadedDocs[result.docType]) {
      return { ...prev, kbisDocsPool: kbisPool, identitySlotByUrl: {}, identityAmbiguousUrls: [] };
    }
    return {
      uploadedDocs: { ...prev.uploadedDocs, [result.docType]: result },
      extractedFields: prev.extractedFields,
      identityDocsPool: prev.identityDocsPool,
      kbisDocsPool: kbisPool,
      identitySlotByUrl: {},
      identityAmbiguousUrls: [],
    };
  }

  // Pièce non-identité : gardes historiques carte grise / contrôle technique.
  const existing = prev.uploadedDocs[result.docType];
  // Deux faces d'une même carte grise : on ne choisit plus, on fusionne — la
  // face principale (immatriculation / VIN) sert de base, l'autre comble ses
  // trous. Cf. `mergeCarteGriseFaces` et le cas GL-911-WF.
  const cgFaces =
    result.docType === 'carte_grise' && existing && cgHasMainFace(existing) !== cgHasMainFace(result)
      ? cgHasMainFace(existing)
        ? mergeCarteGriseFaces(existing, result)
        : mergeCarteGriseFaces(result, existing)
      : null;
  const keepExistingFace =
    !cgFaces &&
    ((result.docType === 'carte_grise' && !cgHasVehicleIdentity(result) && cgHasVehicleIdentity(existing)) ||
      ctSlotKeepsExisting(existing, result));
  const uploadedDocs = cgFaces
    ? { ...prev.uploadedDocs, carte_grise: cgFaces }
    : keepExistingFace
      ? prev.uploadedDocs
      : { ...prev.uploadedDocs, [result.docType]: result };
  const extractedFields = mergeExtractedFields(prev.extractedFields, result.ocrResponse, result.docType, options);

  // Pièce de référence des rôles : les identités déjà déposées peuvent enfin
  // être rattachées à la bonne personne → re-routage du pool.
  if (IDENTITY_REF_DOC_TYPES.includes(result.docType) && prev.identityDocsPool.length > 0) {
    return { ...applyIdentityRouting(uploadedDocs, extractedFields, prev.identityDocsPool, options), kbisDocsPool: kbisPool };
  }

  // Cession / carte grise : les identifiants de chaque partie viennent d'arriver,
  // un Kbis déposé avant elles peut enfin être attribué.
  if (KBIS_REF_DOC_TYPES.includes(result.docType) && kbisPool.length > 0) {
    return {
      ...applyKbisRouting(uploadedDocs, extractedFields, kbisPool, options),
      identityDocsPool: prev.identityDocsPool,
    };
  }

  return {
    uploadedDocs,
    extractedFields,
    identityDocsPool: prev.identityDocsPool,
    kbisDocsPool: kbisPool,
    identitySlotByUrl: {},
    identityAmbiguousUrls: [],
  };
};

/**
 * Intègre un résultat OCR, en tranchant au passage QUELLE cession fait foi.
 *
 * Un dossier de revente porte plusieurs certificats de cession (chaîne de
 * propriété) pour un seul slot. Ils entrent donc dans un pool, et `elireCession`
 * désigne celui du dossier ; les autres restent visibles en « Autres pièces
 * reçues » sans alimenter les champs. L'élection est recalculée à l'arrivée du
 * Cerfa 13750 ou de la carte grise, qui sont ce qui permet de trancher.
 */
export const integrateDocResult = (
  prev: Pick<IntegratedDocState, 'uploadedDocs' | 'extractedFields' | 'identityDocsPool'> &
    Partial<Pick<IntegratedDocState, 'kbisDocsPool' | 'cessionDocsPool'>>,
  result: UploadedDocResult,
  options: { trace?: boolean } = {},
): IntegratedDocState => {
  const cessionPool = prev.cessionDocsPool ?? [];
  const isPlaceholder = Object.keys(result.ocrResponse.fields).length === 0;

  // Une cession de plus au dossier : elle entre au pool (éviction par URL pour
  // la ré-analyse d'un même fichier), puis l'élection est rejouée.
  if (result.docType === 'cerfa_cession' && !isPlaceholder) {
    const url = result.firebaseUrls[0] ?? `cerfa_cession-${cessionPool.length}`;
    const pool: CessionPoolEntry[] = [
      ...cessionPool.filter((e) => (e.doc.firebaseUrls[0] ?? '') !== url),
      { doc: { ...result } },
    ];
    // Le passage par la voie normale garde les effets de bord attendus d'une
    // cession (re-routage des identités et des Kbis, qu'elle référence).
    const base = integrateDocResultSansCession({ ...prev, cessionDocsPool: pool }, result, options);
    return { ...base, ...applyCessionElection(base.uploadedDocs, base.extractedFields, pool, options) };
  }

  const base = integrateDocResultSansCession(prev, result, options);

  // Pièce de référence de l'élection : le 13750 nomme l'acquéreur, la carte
  // grise le vendeur sortant. Leur arrivée peut faire changer la cession élue.
  if (CESSION_REF_DOC_TYPES.includes(result.docType) && cessionPool.length > 1) {
    return { ...base, ...applyCessionElection(base.uploadedDocs, base.extractedFields, cessionPool, options) };
  }

  return { ...base, cessionDocsPool: cessionPool };
};

/**
 * Réassignation MANUELLE d'une pièce d'identité à une personne. Le choix devient
 * un `pinnedPerson` — au même titre qu'un dépôt en zone dédiée, il n'est plus
 * jamais remis en cause par le matching automatique, et il survit à l'arrivée
 * des pièces de référence (qui relancent le routage).
 *
 * Indispensable pour les versos indécidables : sur une CNI ancien format, le
 * verso ne porte ni nom ni n° de document (la MRZ est au recto), donc aucun
 * algorithme ne peut dire à qui il appartient — seul le déposant le sait.
 * Pur → testable directement.
 */
export const applyIdentityPersonChoice = (
  prev: Pick<IntegratedDocState, 'uploadedDocs' | 'extractedFields' | 'identityDocsPool'>,
  imageUrl: string,
  person: IdentityPerson,
  options: { trace?: boolean } = {},
): IntegratedDocState => {
  const pool = prev.identityDocsPool.map((entry) =>
    (entry.doc.firebaseUrls[0] ?? '') === imageUrl ? { ...entry, pinnedPerson: person } : entry,
  );
  return applyIdentityRouting(prev.uploadedDocs, prev.extractedFields, pool, options);
};

/**
 * Construit le payload de persistance du routage : une entrée PAR PIÈCE DU
 * POOL (et non par slot assigné), pour que les pièces délogées d'un slot
 * repassent explicitement à `routedDocType: null` en base. La clé côté serveur
 * est (imageUrl, docTypeDetected) — `docTypeDetected` = le type persisté à
 * l'extraction (qui peut déjà être une variante rôle imposée par la zone de
 * dépôt dédiée). Pur → testable directement.
 */
/**
 * Famille d'un docType routable : deux pièces ne peuvent partager un slot que si
 * elles appartiennent à la même famille. `identityKindOf` couvre les pièces
 * d'identité (cni / permis) ; le Kbis forme sa propre famille.
 */
const routingFamilyOf = (docType: CgDocType): string | null =>
  identityKindOf(docType) ?? (isKbisDocType(docType) ? 'kbis' : null);

export const buildRoutingUpdates = (
  pool: Array<{ doc: UploadedDocResult }>,
  identitySlotByUrl: Record<string, CgDocType>,
  identitySlotByPoolKey: Record<string, CgDocType> = {},
): Array<{ imageUrl: string; docTypeDetected: CgDocType; routedDocType: CgDocType | null }> =>
  pool.flatMap((entry) => {
    const imageUrl = entry.doc.firebaseUrls[0];
    if (!imageUrl) return [];
    // Fichier multi-pièces : deux entrées du pool (CNI + permis) partagent la
    // même URL. `identitySlotByPoolKey` les distingue — chacune persiste SON
    // slot. Sans lui (routage Kbis, état d'une version antérieure), on retombe
    // sur `identitySlotByUrl`, qui ne porte qu'un slot par URL : le slot n'est
    // alors persisté que pour l'entrée de la MÊME famille, l'autre repartant à
    // null plutôt qu'avec un slot d'une autre famille (que le backend
    // rejetterait, et qui écraserait la mauvaise ligne).
    const kind = identityKindOf(entry.doc.docType);
    const slot =
      (kind ? identitySlotByPoolKey[identityPoolKey(imageUrl, kind)] : undefined) ??
      identitySlotByUrl[imageUrl] ??
      null;
    const slotMatchesEntry = slot !== null && routingFamilyOf(slot) === routingFamilyOf(entry.doc.docType);
    return [
      {
        imageUrl,
        docTypeDetected: entry.doc.docType,
        routedDocType: slotMatchesEntry ? slot : null,
      },
    ];
  });

/**
 * Payload de persistance de l'ÉLECTION DE LA CESSION.
 *
 * Le backend agrège les extractions pour la review : sans cette persistance, il
 * continuerait de mélanger les champs des cessions de la chaîne alors que le
 * formulaire, lui, n'en retient qu'une. On réutilise `routed_doc_type` — la
 * cession élue porte son propre type, les autres repartent à `null`, et le
 * backend les écarte comme il écarte déjà une pièce d'identité sans personne.
 */
export const buildCessionRoutingUpdates = (
  pool: ReadonlyArray<{ doc: UploadedDocResult }>,
  urlElue: string | null,
): Array<{ imageUrl: string; docTypeDetected: CgDocType; routedDocType: CgDocType | null }> => {
  if (pool.length < 2) return []; // Une seule cession : rien à départager.
  return pool.flatMap((entry) => {
    const imageUrl = entry.doc.firebaseUrls[0];
    if (!imageUrl) return [];
    return [
      {
        imageUrl,
        docTypeDetected: 'cerfa_cession' as CgDocType,
        routedDocType: imageUrl === urlElue ? ('cerfa_cession' as CgDocType) : null,
      },
    ];
  });
};

/**
 * Persistance débouncée du routage (personne × face) vers
 * PATCH /api/cg-extractions/routing. Le routage est recalculé intégralement à
 * chaque arrivée de pièce : seul le DERNIER état compte, d'où le debounce.
 * Fire-and-forget : un échec ne bloque jamais le wizard (la review retombe
 * alors sur le type générique, comportement d'avant cette persistance).
 */
const ROUTING_PERSIST_DEBOUNCE_MS = 1_500;
const routingPersistTimer: { id: ReturnType<typeof setTimeout> | null } = { id: null };
const schedulePersistIdentityRouting = (
  pool: Array<{ doc: UploadedDocResult }>,
  identitySlotByUrl: Record<string, CgDocType>,
  identitySlotByPoolKey: Record<string, CgDocType> = {},
  extraRoutings: Array<{ imageUrl: string; docTypeDetected: CgDocType; routedDocType: CgDocType | null }> = [],
): void => {
  const routings = [...buildRoutingUpdates(pool, identitySlotByUrl, identitySlotByPoolKey), ...extraRoutings];
  if (routings.length === 0) return;
  if (routingPersistTimer.id !== null) clearTimeout(routingPersistTimer.id);
  routingPersistTimer.id = setTimeout(async () => {
    routingPersistTimer.id = null;
    try {
      await makeAuthenticatedApiCall('patch', '/api/cg-extractions/routing', { routings });
    } catch (error) {
      console.warn('cg-extractions/routing failed (non-blocking):', error);
    }
  }, ROUTING_PERSIST_DEBOUNCE_MS);
};

/**
 * Réétiquette les items d'ingestion d'identité avec le slot routé (le fichier
 * apparaît dans la checklist sous « Pièce d'identité co-titulaire — VERSO »
 * plutôt que sous un « Pièce d'identité » générique).
 */
export const retagIngestionItems = (
  items: Record<string, IngestionItem>,
  identitySlotByUrl: Record<string, CgDocType>,
): Record<string, IngestionItem> => {
  if (Object.keys(identitySlotByUrl).length === 0) return items;
  let changed = false;
  const next: Record<string, IngestionItem> = { ...items };
  Object.values(items).forEach((item) => {
    const slot = item.firebaseUrl ? identitySlotByUrl[item.firebaseUrl] : undefined;
    if (
      slot &&
      item.docTypeDetected &&
      item.docTypeDetected !== 'unknown' &&
      (isIdentitySlotType(item.docTypeDetected) || isKbisDocType(item.docTypeDetected)) &&
      item.docTypeDetected !== slot
    ) {
      next[item.id] = { ...item, docTypeDetected: slot };
      changed = true;
    }
  });
  return changed ? next : items;
};

export const useFormCGIAStore = create<FormCGIAState>()(
  persist(
    (set, get) => ({
      ...INITIAL_STATE,

      startWizard: () =>
        set({
          currentStep: 'uploading_doc',
          currentDocIndex: 0,
          uploadedDocs: {},
          extractedFields: {},
          identityDocsPool: [],
          rejectionReason: null,
        }),

      setCurrentStep: (step) => set({ currentStep: step }),

      setDocQueue: (queue) => set({ docQueue: queue }),

      recordDocResult: (result) =>
        set((state) => {
          // Intégration partagée (cf. integrateDocResult) :
          //  - pièces d'identité → pool + routage (personne × type × face) ;
          //  - carte grise / CT : gardes historiques recto-verso / contre-visite ;
          //  - pièces de référence (13750, cession, mandat, attestation) :
          //    déclenchent un re-routage des identités déjà déposées.
          const integrated = integrateDocResult(
            {
              uploadedDocs: state.uploadedDocs,
              extractedFields: state.extractedFields,
              identityDocsPool: state.identityDocsPool,
              kbisDocsPool: state.kbisDocsPool,
            },
            result,
          );
          // Persistance du routage en base (cg_ocr_extractions.routed_doc_type),
          // uniquement quand un passage de routage vient d'avoir lieu — un
          // identitySlotByUrl vide signifie « pas de re-routage », pas
          // « effacer les slots persistés ».
          const hasRouted =
            Object.keys(integrated.identitySlotByUrl).length > 0 || integrated.cessionDocsPool.length > 1;
          if (hasRouted) {
            schedulePersistIdentityRouting(
              [...integrated.identityDocsPool, ...integrated.kbisDocsPool],
              integrated.identitySlotByUrl,
              integrated.identitySlotByPoolKey,
              buildCessionRoutingUpdates(
                integrated.cessionDocsPool,
                integrated.uploadedDocs.cerfa_cession?.firebaseUrls[0] ?? null,
              ),
            );
          }
          return {
            uploadedDocs: integrated.uploadedDocs,
            extractedFields: integrated.extractedFields,
            identityDocsPool: integrated.identityDocsPool,
            kbisDocsPool: integrated.kbisDocsPool,
            identityAmbiguousUrls: hasRouted ? integrated.identityAmbiguousUrls : state.identityAmbiguousUrls,
            ingestionItems: retagIngestionItems(state.ingestionItems, integrated.identitySlotByUrl),
            rejectionReason: null,
          };
        }),

      reassignIdentityPerson: (imageUrl, person) =>
        set((state) => {
          const integrated = applyIdentityPersonChoice(
            {
              uploadedDocs: state.uploadedDocs,
              extractedFields: state.extractedFields,
              identityDocsPool: state.identityDocsPool,
            },
            imageUrl,
            person,
            { trace: true },
          );
          // Le choix manuel doit atteindre la base : sans cela, /review
          // ré-afficherait le slot deviné par défaut à la relecture.
          schedulePersistIdentityRouting(
            integrated.identityDocsPool,
            integrated.identitySlotByUrl,
            integrated.identitySlotByPoolKey,
          );
          return {
            uploadedDocs: integrated.uploadedDocs,
            extractedFields: integrated.extractedFields,
            identityDocsPool: integrated.identityDocsPool,
            identityAmbiguousUrls: integrated.identityAmbiguousUrls,
            ingestionItems: retagIngestionItems(state.ingestionItems, integrated.identitySlotByUrl),
          };
        }),

      setRejection: (reason) => set({ rejectionReason: reason }),

      advanceToNextDoc: () =>
        set((state) => {
          // Cas re-upload d'un seul doc : on retourne directement au review
          // sans ré-itérer sur les docs suivants dans la queue.
          if (state.reuploadMode) {
            return { currentStep: 'review_form', reuploadMode: null };
          }
          const nextIndex = state.currentDocIndex + 1;
          if (nextIndex >= state.docQueue.length) {
            return { currentStep: 'cross_checking', currentDocIndex: nextIndex };
          }
          return { currentStep: 'uploading_doc', currentDocIndex: nextIndex };
        }),

      jumpToReuploadDoc: (docType) =>
        set((state) => {
          const idx = state.docQueue.indexOf(docType);
          if (idx === -1) return state;

          // Retire le doc ciblé de `uploadedDocs`
          const newUploadedDocs = { ...state.uploadedDocs };
          delete newUploadedDocs[docType];

          // Retire de `extractedFields` tous les champs venant de ce docType —
          // sinon d'anciennes valeurs OCR polluaient la review après re-upload.
          const newExtractedFields: Record<string, ExtractedFieldEntry> = {};
          Object.entries(state.extractedFields).forEach(([key, entry]) => {
            if (entry.sourceDocType !== docType) {
              newExtractedFields[key] = entry;
            }
          });

          return {
            uploadedDocs: newUploadedDocs,
            extractedFields: newExtractedFields,
            currentStep: 'uploading_doc',
            currentDocIndex: idx,
            reuploadMode: docType,
            rejectionReason: null,
          };
        }),

      setHebergementUploads: (uploads) => set({ hebergementUploads: uploads }),
      setCoBuyerFiles: (urls) => set({ coBuyerFiles: urls }),
      setAgencyComment: (comment) => set({ agencyComment: comment }),
      setAgencyCommentFiles: (urls) => set({ agencyCommentFiles: urls }),

      setKbisUrls: (urls) => set({ kbisUrls: urls }),

      reset: () => set({ ...INITIAL_STATE }),

      // ── Actions v2 ──

      startV2: () =>
        set({
          v2Step: 'plate_and_dept',
          ingestionItems: {},
          uploadedDocs: {},
          extractedFields: {},
          identityDocsPool: [],
          parcours: null,
          rejectionReason: null,
          regPlate: '',
          department: '',
          isSuccession: false,
          isProfessionalResale: false,
          entityKindOverrides: {},
          taxData: null,
          taxConfirmReturn: 'ingesting',
        }),

      setV2Step: (step) => set({ v2Step: step }),

      enqueueIngestionItems: (items) =>
        set((state) => {
          const next = { ...state.ingestionItems };
          items.forEach((it) => {
            next[it.id] = it;
          });
          return { ingestionItems: next };
        }),

      updateIngestionItem: (id, patch) =>
        set((state) => {
          const existing = state.ingestionItems[id];
          if (!existing) return state;
          return {
            ingestionItems: { ...state.ingestionItems, [id]: { ...existing, ...patch } },
          };
        }),

      removeIngestionItem: (id) =>
        set((state) => {
          const next = { ...state.ingestionItems };
          delete next[id];
          return { ingestionItems: next };
        }),

      setParcours: (parcours) => set({ parcours }),

      setAnalysisSnapshot: (analysisSnapshot) => set({ analysisSnapshot }),

      setPlateAndDepartment: (regPlate, department) => set({ regPlate, department }),

      setIsSuccession: (value) => set({ isSuccession: value }),

      setIsProfessionalResale: (value) => set({ isProfessionalResale: value }),

      // `null` rend la main à la règle plutôt que de figer la valeur déduite :
      // un dossier qu'on a forcé puis relâché doit repartir de ce que disent
      // les pièces, pas d'une photographie prise au moment du clic.
      setEntityKindOverride: (partie, nature) =>
        set((state) => {
          const next = { ...state.entityKindOverrides };
          if (nature === null) delete next[partie];
          else next[partie] = nature;
          return { entityKindOverrides: next };
        }),

      setTaxData: (taxData) => set({ taxData }),

      setTaxConfirmReturn: (target) => set({ taxConfirmReturn: target }),

      updateExtractedFieldValue: (fieldKey, value) =>
        set((state) => {
          const existing = state.extractedFields[fieldKey];
          // Champ existant : on remplace sa valeur en conservant sa source
          // documentaire et on force la confiance à 1 (saisie humaine explicite
          // = vérité). Champ absent (cas rare : pièce illisible mais saisie
          // manuelle de l'agence) : on crée une entrée rattachée par défaut à
          // la carte grise — la provenance n'est plus déterminante une fois
          // l'ingestion terminée, mais reste un `CgDocType` valide.
          const nextEntry: ExtractedFieldEntry = existing
            ? { ...existing, field: { ...existing.field, value, confidence: 1, source: 'extracted' } }
            : { fieldKey, field: { value, confidence: 1, source: 'extracted' }, sourceDocType: 'carte_grise' };
          return { extractedFields: { ...state.extractedFields, [fieldKey]: nextEntry } };
        }),

      setActiveInFlightId: (id: string | null) => set({ activeInFlightId: id }),

      serializeV2Snapshot: (): V2SessionSnapshot => {
        const state = get();
        return {
          regPlate: state.regPlate,
          department: state.department,
          taxData: state.taxData,
          v2Step: state.v2Step,
          parcours: state.parcours,
          isSuccession: state.isSuccession,
          isProfessionalResale: state.isProfessionalResale,
          entityKindOverrides: state.entityKindOverrides,
          ingestionItems: state.ingestionItems,
          uploadedDocs: state.uploadedDocs,
          extractedFields: state.extractedFields,
          identityDocsPool: state.identityDocsPool,
        };
      },

      hydrateV2FromSnapshot: (snapshot: V2SessionSnapshot, inFlightId: string) =>
        set({
          regPlate: snapshot.regPlate ?? '',
          department: snapshot.department ?? '',
          taxData: snapshot.taxData ?? null,
          // Si le snapshot est antérieur à `ingesting`, on bascule directement
          // l'utilisateur sur l'écran de dépôt pour qu'il voie la suite logique.
          v2Step: snapshot.v2Step ?? 'ingesting',
          parcours: snapshot.parcours ?? null,
          isSuccession: snapshot.isSuccession ?? false,
          isProfessionalResale: snapshot.isProfessionalResale ?? false,
          entityKindOverrides: snapshot.entityKindOverrides ?? {},
          ingestionItems: snapshot.ingestionItems ?? {},
          uploadedDocs: snapshot.uploadedDocs ?? {},
          extractedFields: snapshot.extractedFields ?? {},
          identityDocsPool: snapshot.identityDocsPool ?? [],
          // Les pièces « à rattacher manuellement » appartiennent au dossier
          // qu'on quitte : sans ce reset elles restaient affichées sur le
          // dossier repris, qui n'a aucune ambiguïté à trancher.
          identityAmbiguousUrls: [],
          activeInFlightId: inFlightId,
        }),

      hydrateV2FromAgencyEditContext: (ctx: AgencyEditContext) => {
        // Mode édition : on saute directement à l'étape ingesting avec :
        //  - les pièces déjà OK pré-remplies (lecture seule)
        //  - les openRequests qui flagent les pièces à redéposer
        //  - le nextStatusOnFulfill pour anticiper la transition UI
        const validatedLockedDocTypes: CgDocType[] = (Object.keys(ctx.uploadedDocs) as CgDocType[]).filter(
          (docType) => !ctx.openRequests.some((req) => req.docType === docType),
        );
        set({
          editingRegistrationId: ctx.registrationId,
          regPlate: ctx.regPlate ?? '',
          department: ctx.department ?? '',
          taxData: ctx.taxData ?? null,
          parcours: ctx.parcours ?? null,
          uploadedDocs: ctx.uploadedDocs ?? {},
          extractedFields: ctx.extractedFields ?? {},
          identityDocsPool: [],
          openDocumentRequests: ctx.openRequests ?? [],
          validatedLockedDocTypes,
          nextStatusOnFulfill: ctx.nextStatusOnFulfill,
          // Édition = pas de session in-flight active, pas de queue d'ingestion en cours.
          v2Step: 'ingesting',
          ingestionItems: {},
          activeInFlightId: null,
        });
      },

      // Repart de INITIAL_STATE, typé exhaustif (Omit<FormCGIAState, actions>) :
      // tout champ ajouté au store est remis à zéro sans avoir à penser à ce
      // reset. L'ancienne liste à la main avait oublié `kbisDocsPool` (ajouté
      // le 2026-09-15) : le Kbis d'un dossier se recollait sur tous les
      // dossiers suivants de l'onglet (Biwiz, 24-25/09/2026 : L'AUTOBUS
      // partout en vendeur/acheteur).
      resetV2: () =>
        set({
          ...INITIAL_STATE,
          docQueue: [...DEFAULT_DOCUMENT_QUEUE],
          hebergementUploads: { ...EMPTY_HEBERGEMENT },
        }),
    }),
    {
      name: 'form_cgia_session',
      storage: createJSONStorage(() => sessionStorage),
      // Bump de version quand on change la forme d'un champ persisté.
      // v2 (2026-06) : ajout de `is_stub` dans `taxData.vehicleDetails`. Les
      // sessions ouvertes avant ce déploiement ont un `taxData` sans le flag,
      // ce qui empêche `crossCheckRules.isMisivStubResponse` de rétrograder
      // `vin_misiv_mismatch` en INFO en dev — la règle voit `undefined` au
      // lieu de `true` et émet une MAJEURE erronée. On purge donc `taxData`
      // (forçant un nouveau call MiSIV via PlateAndDepartmentScreen) sans
      // toucher au reste de la session en cours.
      version: 2,
      migrate: (persistedState: unknown, version: number) => {
        const state = (persistedState ?? {}) as Record<string, unknown>;
        if (version < 2) {
          state.taxData = null;
        }
        // Zustand persist accepte un état partiel ici ; les champs absents
        // seront complétés par les defaults du store à l'hydratation.
        return state as unknown as FormCGIAState;
      },
    },
  ),
);
